import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { AnswerBlock, MessageDraft } from '../../models/message';
import { ChatStrings } from '../../i18n';
import MessageItem from '../MessageItem';
import { prefersReducedMotion } from '../AnswerPhotos';
import { advanceReveal, isTyping, retarget, Reveal, startReveal, textLength, visibleBlocks } from './reveal';

interface TypedDraftProps {
   draft: MessageDraft;
   strings: ChatStrings;
   /** Called before paint each time the page grew (a frame of typing, or a whole draft with reduced motion). */
   onGrow?: () => void;
}

/** A draft's blocks; a draft with only the plain text types as one paragraph. */
const draftBlocks = (draft: MessageDraft): AnswerBlock[] => (draft.blocks.length > 0 ? draft.blocks : [{ kind: 'text', text: draft.text.trim() }]);

/** Types `target` out frame by frame (see `reveal.ts`) and returns the blocks on screen. */
export function useTypedBlocks(target: AnswerBlock[]): AnswerBlock[] {
   const [reveal, setReveal] = useState<Reveal>(() => startReveal(target, prefersReducedMotion()));
   // A newer draft takes effect in this very render, so a restarted answer never shows its old text for a frame
   let current = reveal;
   if (reveal.target !== target) {
      current = retarget(reveal, target, prefersReducedMotion());
      setReveal(current);
   }

   const typing = isTyping(current);
   useEffect(() => {
      if (!typing) return;
      let frame = 0;
      let last: number | undefined;
      const step = (time: number) => {
         const elapsed = last === undefined ? 0 : time - last;
         last = time;
         if (elapsed > 0) setReveal((prev) => advanceReveal(prev, elapsed));
         frame = requestAnimationFrame(step);
      };
      frame = requestAnimationFrame(step);
      return () => cancelAnimationFrame(frame);
   }, [typing]);

   const shown = Math.floor(current.revealed);
   return useMemo(() => visibleBlocks(current.target, shown), [current.target, shown]);
}

/**
 * An answer still being written, typed out as it comes: the page its answer will be (the blocks so far, no photos,
 * sources, reception or Retry). Only this page re-renders while it types, never the column.
 */
const TypedDraft: React.FC<TypedDraftProps> = ({ draft, strings, onGrow }) => {
   const target = useMemo(() => draftBlocks(draft), [draft]);
   const blocks = useTypedBlocks(target);
   const length = textLength(blocks);

   useLayoutEffect(() => {
      if (length > 0) onGrow?.();
   }, [length, onGrow]);

   return <MessageItem message={{ sender: 'bot', text: '', blocks, timestamp: '' }} strings={strings} />;
};

export default TypedDraft;
